import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { analyticsApi, ApiRequestError, type HeatmapDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  formatScore,
  lineTitle,
  scoreToColor,
  scoreToText,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

function HeatCell({
  score,
  title,
  belowTarget,
}: {
  score: number | null;
  title: string;
  belowTarget?: boolean;
}) {
  return (
    <td className="sm-heat-td">
      <span
        className={[
          "sm-heat-cell",
          score === null ? "is-empty" : "",
          belowTarget ? "is-below" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ background: scoreToColor(score), color: scoreToText(score) }}
        title={title}
      >
        {formatScore(score)}
      </span>
    </td>
  );
}

export function HeatMapPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<HeatmapDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void analyticsApi
      .heatmap(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  const grouped = useMemo(() => {
    const map = new Map<string, HeatmapDto["rows"]>();
    for (const row of data?.rows ?? []) {
      const key = row.line.macroProcess || "—";
      const list = map.get(key) ?? [];
      list.push(row);
      map.set(key, list);
    }
    return [...map.entries()];
  }, [data]);

  const threshold = data?.threshold ?? 2;

  return (
    <div className="sm-page">
      <PageHeader title={t("heat_map")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} showUser />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : !data ? null : (
        <div className="sm-card sm-card-pad sm-heatmap-wrap">
          <div className="sm-analytics-meta">
            <span className="sm-muted">
              {data.rows.length} lignes · {data.users.length} experts
            </span>
            <div className="sm-score-legend sm-score-legend-rich">
              <span className="sm-muted">0</span>
              <div className="sm-score-legend-bar" aria-hidden>
                {[0, 1, 2, 3, 4].map((s) => (
                  <i key={s} style={{ background: scoreToColor(s) }} />
                ))}
              </div>
              <span className="sm-muted">4</span>
              <span className="sm-muted sm-score-legend-sep">
                {t("target_threshold")}: <strong>{threshold}</strong>
              </span>
            </div>
          </div>
          <div className="sm-table-wrap sm-heatmap-scroll">
            <table className="sm-heatmap-table sm-heatmap-grid">
              <thead>
                <tr>
                  <th className="sm-heat-label-col">BPML</th>
                  <th className="sm-heat-avg-col">Avg</th>
                  {data.users.map((u) => (
                    <th key={u.id} className="sm-heat-user" title={u.name}>
                      <span>{u.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(([macro, rows]) => (
                  <Fragment key={macro}>
                    <tr>
                      <td colSpan={2 + data.users.length} className="sm-heat-group">
                        {macro}
                      </td>
                    </tr>
                    {rows.map((row) => {
                      const { title, subtitle } = lineTitle(row.line);
                      return (
                        <tr key={row.line.id}>
                          <td className="sm-heat-label-col">
                            <div className="sm-heat-line-title">{title}</div>
                            <div className="sm-heat-line-sub">{subtitle}</div>
                          </td>
                          <HeatCell
                            score={row.avg}
                            title={`${t("avg_score")}: ${formatScore(row.avg)}`}
                            belowTarget={row.avg !== null && row.avg < threshold}
                          />
                          {row.scores.map((s, j) => {
                            const name = data.users[j]?.name ?? "";
                            return (
                              <HeatCell
                                key={j}
                                score={s.score}
                                title={`${name}: ${formatScore(s.score)}`}
                                belowTarget={s.score !== null && s.score < threshold}
                              />
                            );
                          })}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={2 + data.users.length} className="sm-muted" style={{ textAlign: "center" }}>
                      {t("no_data")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
